import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { JwtService } from "./jwt.service";
import { GlobalService } from "./global.service";
import { Router } from "@angular/router";
@Injectable()
export class ApiService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private jwtService: JwtService,
    public globalService: GlobalService
  ) {}

  private setHeaders(preferPortalAuth = false): HttpHeaders {
    const headersConfig = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    const authToken = this.jwtService.getToken();
    const checkInToken = window.localStorage["CheckIn_TOKEN"] || window.sessionStorage["CheckIn_TOKEN"];
    const loginToken = (
      window.localStorage.getItem('LogIn_TOKEN') || window.sessionStorage.getItem('LogIn_TOKEN')
    )?.replace(/^"|"$/g, '');
    const isPortalRoute = this.router.url.startsWith('/portal');

    if (preferPortalAuth) {
      if (authToken) {
        headersConfig["Authorization"] = authToken;
      }
      return new HttpHeaders(headersConfig);
    }

    if (isPortalRoute && authToken) {
      headersConfig["Authorization"] = authToken;
    } else if (checkInToken) {
      headersConfig["Authorization"] = checkInToken;
    } else if (loginToken) {
      headersConfig["Authorization"] = loginToken;
    } else if (authToken && authToken.length > 0) {
      headersConfig["Authorization"] = authToken;
    }
    return new HttpHeaders(headersConfig);
  }

  private formatErrors(error: any) {
    return throwError(error);
  }

  /** Last path segment without query/hash — used as permission NameKey fallback. */
  private permissionRouteFromUrl(): string {
    const path = (this.router.url || "").split("?")[0].split("#")[0];
    const parts = path.split("/").filter(Boolean);
    const last = parts.length ? decodeURIComponent(parts[parts.length - 1]) : "";

    if (parts.includes("employee-handbook")) {
      return "Manage-Employee-Handbooks";
    }

    const settingsUrlToPermission: Record<string, string> = {
      AiKey: "Manage-Open-AI",
      "employee-handbook": "Manage-Employee-Handbooks"
    };
    return settingsUrlToPermission[last] || last;
  }

  private assignCommonBodyFields(
    body: any,
    options?: { preserveRequestLangKey?: boolean }
  ): any {
    if (!body || typeof body !== "object" || Array.isArray(body)) {
      return body;
    }
    Object.assign(body, { Subdomain: this.globalService.getHostname() });

    const callerLang = body["LangKey"];
    const keepCallerLang = options?.preserveRequestLangKey && callerLang;

    if (this.globalService.getLangAccToUser() && !keepCallerLang) {
      Object.assign(body, { LangKey: this.globalService.getLangAccToUser() });
    }

    // Do not auto-inject Route on bodies — auth endpoints (login, etc.) reject it.
    // Callers that need permission Route must set it explicitly (withPermissionRoute / withRoute).
    return body;
  }

  private toQueryMap(
    searchParams: HttpParams | Record<string, unknown> = new HttpParams()
  ): Record<string, string> {
    const merged: Record<string, string> = {};
    if (searchParams instanceof HttpParams) {
      searchParams.keys().forEach(key => {
        const val = searchParams.get(key);
        if (val !== null && val !== undefined) {
          merged[key] = String(val);
        }
      });
    } else if (searchParams && typeof searchParams === "object") {
      for (const key of Object.keys(searchParams)) {
        const val = (searchParams as Record<string, unknown>)[key];
        if (val !== undefined && val !== null) {
          merged[key] = String(val);
        }
      }
    }
    return merged;
  }

  private buildQueryParams(
    searchParams: HttpParams | Record<string, unknown> = new HttpParams(),
    options?: { preserveRequestLangKey?: boolean }
  ): HttpParams {
    const merged = this.toQueryMap(searchParams);

    merged.Subdomain = this.globalService.getHostname();
    // Prefer caller Route (permission NameKey); else last URL segment
    if (!merged.Route) {
      merged.Route = this.permissionRouteFromUrl();
    }

    if (this.globalService.getLangAccToUser()) {
      if (options?.preserveRequestLangKey) {
        const callerLang = merged["LangKey"];
        if (callerLang === undefined || callerLang === null || callerLang === "") {
          merged.LangKey = String(this.globalService.getLangAccToUser());
        }
      } else {
        merged.LangKey = String(this.globalService.getLangAccToUser());
      }
    }

    return new HttpParams({ fromObject: merged });
  }

  get<ResultModel>(
    path: string,
    searchParams: HttpParams | Record<string, unknown> = new HttpParams(),
    options?: { preserveRequestLangKey?: boolean; skipGlobalErrorPage?: boolean }
  ): Observable<ResultModel> {
    const params = this.buildQueryParams(searchParams, options);
    let headers = this.setHeaders();

    if (options?.skipGlobalErrorPage) {
      headers = headers.set("x-skip-error-page", "true");
    }

    return this.http
      .get<ResultModel>(`${environment.apiUrl}${path}`, {
        headers,
        params
      })
      .pipe(catchError(this.formatErrors));
  }

  /* Same auth, subdomain and language handling as get(), for endpoints that
     answer with a file rather than JSON. */
  getBlob(
    path: string,
    searchParams: HttpParams | Record<string, unknown> = new HttpParams()
  ): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}${path}`, {
        headers: this.setHeaders(),
        params: this.buildQueryParams(searchParams),
        responseType: "blob"
      })
      .pipe(catchError(this.formatErrors));
  }

  put<ResultModel>(path: string, body: object = {}): Observable<ResultModel> {
    this.assignCommonBodyFields(body);

    return this.http
      .put<ResultModel>(`${environment.apiUrl}${path}`, JSON.stringify(body), {
        headers: this.setHeaders()
      })
      .pipe(catchError(this.formatErrors));
  }

  post<ResultModel>(
    path: string,
    body: Object = {},
    options?: { preferPortalAuth?: boolean; preserveRequestLangKey?: boolean }
  ): Observable<ResultModel> {
    this.assignCommonBodyFields(body, options);

    return this.http
      .post<ResultModel>(`${environment.apiUrl}${path}`, body, {
        headers: this.setHeaders(options?.preferPortalAuth)
      })
      .pipe(catchError(this.formatErrors));
  }

  patch<ResultModel>(
    path: string,
    body: Object = {},
    options?: { preserveRequestLangKey?: boolean }
  ): Observable<ResultModel> {
    this.assignCommonBodyFields(body, options);

    return this.http
      .patch<ResultModel>(`${environment.apiUrl}${path}`, body, {
        headers: this.setHeaders()
      })
      .pipe(catchError(this.formatErrors));
  }

  delete<ResultModel>(
    path: string,
    params: HttpParams | Record<string, unknown> = new HttpParams()
  ): Observable<ResultModel> {
    return this.http
      .delete<ResultModel>(`${environment.apiUrl}${path}`, {
        headers: this.setHeaders(),
        params: this.buildQueryParams(params)
      })
      .pipe(catchError(this.formatErrors));
  }

  deleteWithBody<ResultModel>(path: string, body: Object = {}): Observable<ResultModel> {
    this.assignCommonBodyFields(body);

    return this.http
      .request<ResultModel>('delete', `${environment.apiUrl}${path}`, {
        headers: this.setHeaders(),
        body: body
      })
      .pipe(catchError(this.formatErrors));
  }
}
